import React, { useState, useRef, useCallback, useMemo, useEffect } from "react";
import {
  Table,
  Button,
  Popconfirm,
  notification,
  Space,
  Input,
  Dropdown,
  Menu,
  Checkbox,
  Spin,
} from "antd";
import {
  SearchOutlined,
  EditOutlined,
  PlusOutlined,
  ArrowLeftOutlined,
  FilePdfOutlined,
  RobotOutlined,
  MergeCellsOutlined,
} from "@ant-design/icons";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { fetchDrawings, deleteDrawing, fetchDrawing } from "../services/drawingsAPI";
import { fetchDrawingFolder, updateDrawingFolder, mergeDrawingFolderPDFs } from "../services/drawingFoldersAPI";
import { useAuth } from "../hooks/useAuth";
import CreateDrawingModal from "../components/CreateDrawingModal";
import UpdateDrawingModal from "../components/UpdateDrawingModal";
import DrawingPreview from "../components/DrawingPreview";
import Highlighter from "react-highlight-words";
import { exportDrawingsToPDF } from "../utils/pdfUtils";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { formatDate, formatDateLocal } from "../utils/dateUtils";
import {
  getPreferredPageSize,
  savePreferredPageSize,
} from "../utils/paginationUtils";

const Drawings = () => {
  const { selectedProject } = useAuth();
  const projectId = selectedProject?.project_id;
  const projectName = selectedProject?.project_name;
  const userRole = selectedProject?.role;
  const canUploadDrawings = userRole !== "subcontractor";
  const { folderId, drawingId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const initialPageIndex =
    typeof location.state?.initialPageIndex === "number"
      ? location.state.initialPageIndex
      : 0;

  const [isCreateDrawingModalVisible, setIsCreateDrawingModalVisible] =
    useState(false);
  const [isUpdateDrawingModalVisible, setIsUpdateDrawingModalVisible] =
    useState(false);
  const [selectedDrawing, setSelectedDrawing] = useState(null);
  const queryClient = useQueryClient();
  const [searchFilters, setSearchFilters] = useState({});
  const searchInput = useRef(null);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: getPreferredPageSize("drawings"),
    total: 0,
  });
  const [isAIMode, setIsAIMode] = useState(false);
  const [isAIProcessing, setIsAIProcessing] = useState(false);
  const [showLatestRevisionsOnly, setShowLatestRevisionsOnly] = useState(true);
  const [sortField, setSortField] = useState(null);
  const [deletingDrawingId, setDeletingDrawingId] = useState(null);
  const [sortDirection, setSortDirection] = useState(null);
  const [isMergingPDFs, setIsMergingPDFs] = useState(false);

  // Fetch folder details
  const {
    data: folderData,
    isError: folderError,
    error: folderErrorDetails,
  } = useQuery({
    queryKey: ["drawingFolder", projectId, folderId],
    queryFn: () => fetchDrawingFolder(projectId, folderId),
    enabled: !!projectId && !!folderId,
  });

  React.useEffect(() => {
    if (folderData?.show_latest_revisions_only !== undefined) {
      setShowLatestRevisionsOnly(folderData.show_latest_revisions_only);
    }
  }, [folderData]);

  // Fetch drawings for the selected folder
  const {
    data: drawingsResponse,
    isLoading: drawingsLoading,
    isError: drawingsError,
    error: drawingsErrorDetails,
  } = useQuery({
    queryKey: [
      "drawings",
      projectId,
      folderId,
      pagination.current,
      pagination.pageSize,
      searchFilters,
      showLatestRevisionsOnly,
      sortField,
      sortDirection,
    ],
    queryFn: () =>
      fetchDrawings(projectId, folderId, {
        skip: (pagination.current - 1) * pagination.pageSize,
        limit: pagination.pageSize,
        filters:
          Object.keys(searchFilters).length > 0
            ? JSON.stringify(searchFilters)
            : null,
        show_latest_revisions_only: showLatestRevisionsOnly,
        sort_field: sortField,
        sort_order: sortDirection,
        include_annotations: false,
      }),
    enabled: !!projectId && !!folderId && !drawingId,
    keepPreviousData: true,
    refetchOnMount: true,
    refetchOnWindowFocus: true,
    // While an AI bulk upload is processing in the background, poll so newly
    // created drawings appear without the user having to refresh manually.
    refetchInterval: isAIProcessing ? 5000 : false,
    cacheTime: 0,
    staleTime: 0,
  });

  // AI processing runs server-side for a few minutes with no completion signal,
  // so auto-refresh for a bounded window then stop polling.
  useEffect(() => {
    if (!isAIProcessing) return undefined;
    const timer = setTimeout(() => setIsAIProcessing(false), 3 * 60 * 1000);
    return () => clearTimeout(timer);
  }, [isAIProcessing]);

  const {
    data: directDrawing,
    isLoading: directDrawingLoading,
    isError: directDrawingError,
    error: directDrawingErrorDetails,
  } = useQuery({
    queryKey: ["drawing", projectId, drawingId],
    queryFn: () => fetchDrawing(projectId, drawingId),
    enabled: !!projectId && !!drawingId,
  });

  /** Ordered list for keyboard prev/next drawing when viewing `/drawings/:folderId/:drawingId` */
  const { data: drawingsNavResponse } = useQuery({
    queryKey: [
      "drawingsNav",
      "noAnnotations",
      projectId,
      folderId,
      showLatestRevisionsOnly,
    ],
    queryFn: () =>
      fetchDrawings(projectId, folderId, {
        skip: 0,
        limit: 5000,
        show_latest_revisions_only: showLatestRevisionsOnly,
        sort_field: "drawing_number",
        sort_order: "asc",
        include_annotations: false,
      }),
    enabled: !!projectId && !!folderId && !!drawingId,
    staleTime: 60 * 1000,
  });

  const drawingsNavList = drawingsNavResponse?.drawings ?? [];
  const drawingNavIndex = useMemo(
    () => drawingsNavList.findIndex((d) => d._id === drawingId),
    [drawingsNavList, drawingId]
  );

  const navigateToAdjacentDrawing = useCallback(
    (delta) => {
      const nextIndex = drawingNavIndex + delta;
      if (
        drawingNavIndex < 0 ||
        nextIndex < 0 ||
        nextIndex >= drawingsNavList.length
      ) {
        return;
      }
      const target = drawingsNavList[nextIndex];
      if (target?._id) {
        navigate(`/drawings/${folderId}/${target._id}`, {
          state: location.state,
        });
      }
    },
    [drawingNavIndex, drawingsNavList, folderId, navigate, location.state]
  );

  const onNavigatePreviousDrawing = useCallback(
    () => navigateToAdjacentDrawing(-1),
    [navigateToAdjacentDrawing]
  );

  const onNavigateNextDrawing = useCallback(
    () => navigateToAdjacentDrawing(1),
    [navigateToAdjacentDrawing]
  );

  const onSelectFolderDrawing = useCallback(
    (target, pageIndex = 0) => {
      const targetId = target?._id ?? target?.id;
      if (!targetId) return;
      if (targetId === drawingId) return;
      const targetFolderId = target?.drawing_folder_id ?? folderId;
      navigate(`/drawings/${targetFolderId}/${targetId}`, {
        state: {
          ...(location.state || {}),
          initialPageIndex: pageIndex,
        },
      });
    },
    [drawingId, folderId, navigate, location.state]
  );

  // Extract drawings data and total from the response
  const drawingsData = drawingsResponse?.drawings || [];
  const totalDrawings = drawingsResponse?.total || 0;

  // Update pagination when data changes
  React.useEffect(() => {
    if (totalDrawings !== undefined) {
      setPagination((prev) => ({
        ...prev,
        total: totalDrawings,
      }));
    }
  }, [totalDrawings]);

  // Delete drawing mutation
  const deleteDrawingMutation = useMutation({
    mutationFn: ({ drawingId }) => deleteDrawing(projectId, drawingId),
    onMutate: ({ drawingId }) => {
      setDeletingDrawingId(drawingId);
    },
    onSuccess: () => {
      queryClient.invalidateQueries(["drawings", projectId, folderId]);
      notification.success({ message: "Drawing deleted successfully" });
    },
    onError: (error) => {
      const errorMessage =
        error.response?.data?.detail?.message ||
        error.message ||
        "An unknown error occurred";
      notification.error({
        message: "Failed to delete drawing",
        description: errorMessage,
      });
    },
    onSettled: () => {
      setDeletingDrawingId(null);
    },
  });

  const updateDrawingFolderMutation = useMutation({
    mutationFn: ({ showLatestRevisionsOnly }) =>
      updateDrawingFolder(projectId, folderId, {
        show_latest_revisions_only: showLatestRevisionsOnly,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries(["drawingFolder", projectId, folderId]);
    },
    onError: (error) => {
      const errorMessage =
        error.response?.data?.detail?.message ||
        error.message ||
        "An unknown error occurred";
      notification.error({
        message: "Failed to update drawing folder",
        description: errorMessage,
      });
    },
  });

  const handleExportPDF = async () => {
    try {
      if (!totalDrawings || totalDrawings === 0) {
        notification.warning({ message: "No data to export" });
        return;
      }

      notification.info({
        message: "Exporting PDF...",
        description: "Fetching all records...",
        duration: 2,
      });

      const allDrawingsResponse = await fetchDrawings(projectId, folderId, {
        skip: 0,
        limit: totalDrawings,
        filters:
          Object.keys(searchFilters).length > 0
            ? JSON.stringify(searchFilters)
            : null,
        show_latest_revisions_only: showLatestRevisionsOnly,
        sort_field: sortField,
        sort_order: sortDirection,
        include_annotations: false,
      });

      exportDrawingsToPDF(
        allDrawingsResponse.drawings,
        projectName,
        folderData?.name,
      );
      notification.success({ message: "PDF exported successfully" });
    } catch (error) {
      notification.error({
        message: "Failed to export PDF",
        description: error.message,
      });
    }
  };

  const handleMergeAndDownloadPDFs = async () => {
    try {
      if (!totalDrawings || totalDrawings === 0) {
        notification.warning({ message: "No drawings to merge" });
        return;
      }

      setIsMergingPDFs(true);
      notification.info({
        message: "Merging PDFs...",
        description: "System is merging PDFs, please wait...",
        duration: 3,
      });

      await mergeDrawingFolderPDFs(projectId, folderId, {
        show_latest_revisions_only: showLatestRevisionsOnly,
        filters:
          Object.keys(searchFilters).length > 0
            ? JSON.stringify(searchFilters)
            : null,
        sort_field: sortField,
        sort_order: sortDirection,
        folderName: folderData?.name,
      });

      notification.success({
        message: "PDFs merged successfully",
        description: "The merged PDF has been downloaded.",
      });
    } catch (error) {
      console.error("Error merging PDFs:", error);
      const errorMessage =
        error.response?.data?.detail ||
        error.message ||
        "An error occurred while merging PDFs";
      notification.error({
        message: "Failed to merge PDFs",
        description: errorMessage,
      });
    } finally {
      setIsMergingPDFs(false);
    }
  };

  const previousProjectIdRef = useRef(projectId);

  React.useEffect(() => {
    if (!previousProjectIdRef.current) {
      previousProjectIdRef.current = projectId;
      return;
    }

    if (previousProjectIdRef.current !== projectId) {
      previousProjectIdRef.current = projectId;
      navigate("/drawing-folders");
    }
  }, [projectId, navigate]);

  const handleTableChange = (newPagination, _filters, sorter) => {
    setPagination(newPagination);
    savePreferredPageSize(newPagination.pageSize, "drawings");

    const newField = sorter?.order ? sorter.field : null;
    const newOrder =
      sorter?.order === "ascend"
        ? "asc"
        : sorter?.order === "descend"
          ? "desc"
          : null;

    setSortField(newField);
    setSortDirection(newOrder);
  };

  React.useEffect(() => {
    setPagination((prev) => ({ ...prev, current: 1 }));
  }, [searchFilters]);

  const handleUpdateDrawingClick = (drawing) => {
    setSelectedDrawing(drawing);
    setIsUpdateDrawingModalVisible(true);
  };

  const handleDeleteDrawingClick = (drawingId) => {
    deleteDrawingMutation.mutate({ drawingId });
  };

  const handleSearch = (selectedKeys, confirm, dataIndex) => {
    confirm();
    const searchValue = selectedKeys[0];
    if (searchValue) {
      setSearchFilters((prev) => ({
        ...prev,
        [dataIndex]: searchValue,
      }));
    } else {
      setSearchFilters((prev) => {
        const newFilters = { ...prev };
        delete newFilters[dataIndex];
        return newFilters;
      });
    }
  };

  const handleReset = (clearFilters, confirm) => {
    clearFilters();
    setSearchFilters({});
    confirm();
  };

  const getColumnSearchProps = (dataIndex) => ({
    filterDropdown: ({
      setSelectedKeys,
      selectedKeys,
      confirm,
      clearFilters,
    }) => (
      <div style={{ padding: 8 }}>
        <Input
          ref={(node) => {
            searchInput.current = node;
          }}
          placeholder={`Search ${dataIndex}`}
          value={selectedKeys[0]}
          onChange={(e) =>
            setSelectedKeys(e.target.value ? [e.target.value] : [])
          }
          onPressEnter={() => handleSearch(selectedKeys, confirm, dataIndex)}
          style={{ marginBottom: 8, display: "block" }}
        />
        <Space>
          <Button
            type="primary"
            onClick={() => handleSearch(selectedKeys, confirm, dataIndex)}
            icon={<SearchOutlined />}
            size="small"
            style={{ width: 90 }}
          >
            Search
          </Button>
          <Button
            onClick={() => handleReset(clearFilters, confirm)}
            size="small"
            style={{ width: 90 }}
          >
            Reset
          </Button>
        </Space>
      </div>
    ),
    filterIcon: (filtered) => (
      <SearchOutlined style={{ color: filtered ? "#1890ff" : undefined }} />
    ),
    onFilter: () => true,
    onFilterDropdownOpenChange: (visible) => {
      if (visible) {
        setTimeout(() => searchInput.current?.select(), 100);
      }
    },
    render: (text) => {
      const searchValue = searchFilters[dataIndex];
      return searchValue ? (
        <Highlighter
          highlightStyle={{ backgroundColor: "#ffc069", padding: 0 }}
          searchWords={[searchValue]}
          autoEscape
          textToHighlight={text ? text.toString() : ""}
        />
      ) : (
        text
      );
    },
  });

  const handlePreviewDrawingClick = (drawing) => {
    if (drawing?._id && drawingId !== drawing._id) {
      navigate(`/drawings/${folderId}/${drawing._id}`);
    }
  };

  const getDrawingPreviewUrl = (drawing) => `/drawings/${folderId}/${drawing._id}`;

  const handleLinkedDrawingClick = (event, drawing) => {
    if (
      event.defaultPrevented ||
      event.button !== 0 ||
      event.metaKey ||
      event.ctrlKey ||
      event.shiftKey ||
      event.altKey
    ) {
      return;
    }

    event.preventDefault();
    handlePreviewDrawingClick(drawing);
  };

  const handleOpenCreateModal = (aiMode) => {
    setIsAIMode(aiMode);
    setIsCreateDrawingModalVisible(true);
  };

  if (folderError) {
    notification.error({
      message: "Failed to load folder details",
      description: folderErrorDetails.message,
    });
  }

  if (drawingsError) {
    notification.error({
      message: "Failed to load drawings",
      description: drawingsErrorDetails.message,
    });
  }

  if (directDrawingError) {
    notification.error({
      message: "Failed to load drawing preview",
      description: directDrawingErrorDetails.message,
    });
  }

  const handleCloseDrawingPreview = () => {
    navigate(`/drawing-folders?folder=${encodeURIComponent(folderId)}`);
    queryClient.invalidateQueries(["drawings", projectId, folderId]);
  };

  const handleBackToFolders = () => {
    navigate(`/drawing-folders?folder=${encodeURIComponent(folderId)}`);
  };

  if (drawingId) {
    const drawingHeading = directDrawingLoading
      ? "…"
      : `${directDrawing?.drawing_number ? `${directDrawing.drawing_number} ` : ""}${
          directDrawing?.title || ""
        }`.trim() || (directDrawingError ? "Drawing not found" : "Drawing");

    return (
      <div className="flex flex-col flex-1 min-h-0">
        <h1 className="text-2xl font-bold mb-4 flex-shrink-0">
          <button
            type="button"
            onClick={handleCloseDrawingPreview}
            className="inline border-0 bg-transparent p-0 text-left text-2xl font-bold text-gray-900 cursor-pointer hover:text-blue-600 hover:underline underline-offset-2"
          >
            {folderData?.name || "Folder"}
          </button>
          <span className="text-gray-400 font-normal"> / </span>
          <span>{drawingHeading}</span>
        </h1>

        {directDrawingLoading ? (
          <div className="flex justify-center py-16">
            <Spin size="large" />
          </div>
        ) : directDrawingError || !directDrawing ? (
          <div className="flex flex-col items-center justify-center flex-1 gap-3 py-16 text-center">
            <p className="text-gray-700">
              Could not load this drawing. It may have been deleted or you may
              not have access to it.
            </p>
            <Button type="primary" onClick={handleCloseDrawingPreview}>
              Back to folder
            </Button>
          </div>
        ) : (
          <div className="flex flex-col flex-1 min-h-0">
            <DrawingPreview
              key={directDrawing._id}
              drawing={directDrawing}
              visible={!!directDrawing}
              embedded
              hideEmbeddedTopBar
              onClose={handleCloseDrawingPreview}
              onNavigatePreviousDrawing={onNavigatePreviousDrawing}
              onNavigateNextDrawing={onNavigateNextDrawing}
              folderDrawings={drawingsNavList}
              onSelectFolderDrawing={onSelectFolderDrawing}
              initialPageIndex={initialPageIndex}
            />
          </div>
        )}
      </div>
    );
  }

  const drawingColumns = [
    {
      title: "Drawing Number",
      dataIndex: "drawing_number",
      key: "drawing_number",
      sorter: true,
      sortOrder:
        sortField === "drawing_number"
          ? sortDirection === "asc"
            ? "ascend"
            : sortDirection === "desc"
              ? "descend"
              : null
          : null,
      ...getColumnSearchProps("drawing_number"),
      render: (text, record) => {
        const searchValue = searchFilters.drawing_number;
        return (
          <a
            href={getDrawingPreviewUrl(record)}
            onClick={(event) => handleLinkedDrawingClick(event, record)}
            onMouseDown={(event) => event.stopPropagation()}
          >
            {searchValue ? (
              <Highlighter
                highlightStyle={{ backgroundColor: "#ffc069", padding: 0 }}
                searchWords={[searchValue]}
                autoEscape
                textToHighlight={text ? text.toString() : ""}
              />
            ) : (
              text
            )}
          </a>
        );
      },
    },
    {
      title: "Title",
      dataIndex: "title",
      key: "title",
      ...getColumnSearchProps("title"),
      render: (text, record) => {
        const searchValue = searchFilters.title;
        return (
          <a
            href={getDrawingPreviewUrl(record)}
            onClick={(event) => handleLinkedDrawingClick(event, record)}
            onMouseDown={(event) => event.stopPropagation()}
          >
            {searchValue ? (
              <Highlighter
                highlightStyle={{ backgroundColor: "#ffc069", padding: 0 }}
                searchWords={[searchValue]}
                autoEscape
                textToHighlight={text ? text.toString() : ""}
              />
            ) : (
              text
            )}
          </a>
        );
      },
    },
    {
      title: "Uploaded",
      dataIndex: "created_at",
      key: "created_at",
      sorter: true,
      sortOrder:
        sortField === "created_at"
          ? sortDirection === "asc"
            ? "ascend"
            : sortDirection === "desc"
              ? "descend"
              : null
          : null,
      render: (text) => (text ? formatDateLocal(text) : ""),
    },
    {
      title: "Revision Date",
      dataIndex: "revision_date",
      key: "revision_date",
      sorter: true,
      sortOrder:
        sortField === "revision_date"
          ? sortDirection === "asc"
            ? "ascend"
            : sortDirection === "desc"
              ? "descend"
              : null
          : null,
      render: (text) => (text ? formatDate(text) : ""),
    },
    {
      title: "Revision Title",
      dataIndex: "revision_title",
      key: "revision_title",
      ...getColumnSearchProps("revision_title"),
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, record) => (
        <div
          onClick={(e) => e.stopPropagation()}
          onMouseDown={(e) => e.stopPropagation()}
          onDoubleClick={(e) => e.stopPropagation()}
        >
          {deletingDrawingId === record._id ? (
            <Spin size="small" />
          ) : (
            <Dropdown
              overlay={
                <Menu>
                  <Menu.Item
                    key="1"
                    onClick={({ domEvent }) => domEvent.stopPropagation()}
                  >
                    <Button
                      className="w-full"
                      onClick={(e) => {
                        e.stopPropagation();
                        handlePreviewDrawingClick(record);
                      }}
                    >
                      Preview
                    </Button>
                  </Menu.Item>
                  <Menu.Item
                    key="2"
                    onClick={({ domEvent }) => domEvent.stopPropagation()}
                  >
                    <Button
                      className="w-full"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleUpdateDrawingClick(record);
                      }}
                    >
                      Update
                    </Button>
                  </Menu.Item>
                  <Menu.Item
                    key="3"
                    onClick={({ domEvent }) => domEvent.stopPropagation()}
                  >
                    <Popconfirm
                      title="Are you sure you want to delete this drawing?"
                      onConfirm={() => handleDeleteDrawingClick(record._id)}
                      okText="Yes"
                      cancelText="No"
                      getPopupContainer={(trigger) => trigger.parentElement}
                    >
                      <Button
                        className="w-full"
                        danger
                        onClick={(e) => e.stopPropagation()}
                        loading={deletingDrawingId === record._id}
                      >
                        Delete
                      </Button>
                    </Popconfirm>
                  </Menu.Item>
                </Menu>
              }
              trigger={["click"]}
              getPopupContainer={(trigger) => trigger.parentElement}
            >
              <Button
                icon={<EditOutlined />}
                size="medium"
                onClick={(e) => e.stopPropagation()}
              ></Button>
            </Dropdown>
          )}
        </div>
      ),
    },
  ];

  return (
    <div>
      <div className="flex items-center gap-2 mb-4">
        <Button
          type="link"
          icon={<ArrowLeftOutlined />}
          onClick={handleBackToFolders}
          className="!px-0"
        >
          Drawing Folders
        </Button>
        <span className="text-gray-400">/</span>
        <span>{folderData?.name}</span>
      </div>

      <h1 className="text-2xl font-bold mb-4">
        Drawings in {folderData?.name}
      </h1>

      <div className="flex gap-4 items-center mb-4">
        {canUploadDrawings && (
          <>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => handleOpenCreateModal(false)}
            >
              Add Drawing
            </Button>
            <Button
              type="primary"
              icon={<RobotOutlined />}
              onClick={() => handleOpenCreateModal(true)}
            >
              AI Bulk Upload
            </Button>
          </>
        )}
        <Button
          icon={<FilePdfOutlined />}
          onClick={handleExportPDF}
          disabled={!drawingsData || drawingsData.length === 0}
        >
          Export PDF
        </Button>
        <Button
          icon={<MergeCellsOutlined />}
          onClick={handleMergeAndDownloadPDFs}
          disabled={!drawingsData || drawingsData.length === 0 || isMergingPDFs}
          loading={isMergingPDFs}
        >
          Merge & Download
        </Button>

        <Checkbox
          checked={showLatestRevisionsOnly}
          onChange={(e) => {
            const newValue = e.target.checked;
            setShowLatestRevisionsOnly(newValue);
            updateDrawingFolderMutation.mutate({
              showLatestRevisionsOnly: newValue,
            });
          }}
        >
          Show Latest Revisions Only
        </Checkbox>
      </div>

      <Table
        columns={drawingColumns}
        dataSource={drawingsData}
        rowKey="_id"
        loading={drawingsLoading}
        onRow={(record) => ({
          onClick: () => handlePreviewDrawingClick(record),
          className: "cursor-pointer",
        })}
        pagination={{
          ...pagination,
          total: totalDrawings,
          showSizeChanger: true,
          showQuickJumper: true,
          showTotal: (total) => `Total ${total} items`,
        }}
        onChange={handleTableChange}
      />

      {/* Modals */}
      <CreateDrawingModal
        visible={isCreateDrawingModalVisible}
        onClose={() => setIsCreateDrawingModalVisible(false)}
        folderId={folderId}
        isAIMode={isAIMode}
        onProcessingStarted={() => setIsAIProcessing(true)}
      />

      <UpdateDrawingModal
        visible={isUpdateDrawingModalVisible}
        onClose={() => setIsUpdateDrawingModalVisible(false)}
        drawing={selectedDrawing}
      />

    </div>
  );
};

export default Drawings;
